import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  AlertBanner,
  ApplicationIcon,
  Box,
  ListRow,
  Loader,
  Typography,
} from '@saviynt/design-system';

import IntegrationsSkeletonLoader from '../../../../components/IntegrationsSkeletonLoader/IntegrationsSkeletonLoader';
import {
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
} from '../../../../constants/obNextGenConstants';
import { useLocalization } from '../../../../hooks';
import type { RootState } from '../../../../redux/obNextGenStore';
import type {
  DiscoveryAllTabFilterState,
  IntegrationAPIItem,
} from '../../data-layer/types';
import { useGetAllTabIntegrationsQuery } from '../../data-store/IntegrationAllTabApi';
import {
  ItemContent,
  TrailingContent,
} from '../IntegrationsPageAllTabItems/IntegrationsPageAllTabItems';
import IntegrationAllTabStaticCards from '../IntegrationsPageAllTabStaticCards/IntegrationsPageAllTabStaticCards';
import SearchFilterViewBar from '../SearchFilterViewBar/SearchFilterViewBar';

import './IntegrationsPageAllTab.css';

/**
 * ImageWithFallback component displays an image with a fallback in case of error.
 */
function ImageWithFallback({
  src,
  alt,
}: {
  src: string | undefined;
  alt: string;
}) {
  const [error, setError] = useState(false);

  if (!src || error) {
    return <ApplicationIcon size='large' logo='' />;
  }

  return <img src={src} alt={alt} onError={() => setError(true)} />;
}

/**
 * IntegrationsPageAllTab component renders the “All” tab with infinite scroll,
 * search, filtering, and context-sensitive empty states.
 * It fetches data via RTK Query, handles filter/reset, and prevents flicker.
 */
function IntegrationsPageAllTab(): JSX.Element {
  const [page, setPage] = useState(0);
  const formatMessage = useLocalization();
  const [integrationList, setIntegrationList] = useState<IntegrationAPIItem[]>(
    []
  );
  const [skip, setSkip] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [lastFetchedPage, setLastFetchedPage] = useState<number | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const [statusLoadingIds, setStatusLoadingIds] = useState<Set<number>>(
    new Set()
  );

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Redux-backed filter state
  const { showHiddenApps, searchQuery, selectedTypes } = useSelector<
    RootState,
    DiscoveryAllTabFilterState
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  >((state) => state.discoveryAllTabSearchToggelFilter);

  // Memoize query arguments to avoid unnecessary hook calls
  const queryArgs = useMemo(
    () => ({ page, showHiddenApps, searchQuery, selectedTypes }),
    [page, showHiddenApps, searchQuery, selectedTypes]
  );

  // RTK Query hook: skip fetch during filter-change; auto-refetch on mount/arg change
  const { data, isFetching, isLoading } = useGetAllTabIntegrationsQuery(
    queryArgs,
    {
      skip,
      refetchOnMountOrArgChange: true,
    }
  );

  const baseUrl = useMemo(() => getBaseUrlObNextGen(), []);
  const { GET_APPLICATION_ICONS } = getObNextGenApiUrlLists();

  useEffect(() => {
    if (!isLoading && !isFetching && !skip && data) {
      setHasFetched(true);
    }
  }, [data, isLoading, isFetching, skip]);

  /**
   * Track first successful fetch and update list (replace or append)
   */
  useEffect(() => {
    if (!data || !Array.isArray(data.list)) {
      return;
    }

    setHasFetched(true);
    setLastFetchedPage(page);

    setIntegrationList((prev) => {
      const updatedMap = new Map<number, IntegrationAPIItem>();

      prev.forEach((item) => updatedMap.set(item.id, item));

      data.list.forEach((item) => {
        updatedMap.set(item.id, { ...updatedMap.get(item.id), ...item });
      });

      const prevIds = new Set(prev.map((item) => item.id));
      const newItems = data.list.filter((item) => !prevIds.has(item.id));

      return page === 0
        ? Array.from(updatedMap.values())
        : [...prev, ...newItems];
    });
  }, [data, page]);

  /**
   * On filter change: reset page, clear list, and skip next fetch
   */
  useEffect(() => {
    setIntegrationList([]);
    setPage(0);
    setSkip(true);
    setShowEmptyState(false);
  }, [searchQuery, showHiddenApps, selectedTypes]);

  /**
   * Exit skip mode once reset page fetch is ready
   */
  useEffect(() => {
    if (skip && page === 0) {
      setSkip(false);
    }
  }, [skip, page]);

  /** Disconnect observer on unmount */
  useEffect(() => () => observer.current?.disconnect(), []);

  /**
   * Locally filter hidden vs visible integrations
   */
  const visibleIntegrations = useMemo(
    () =>
      integrationList.filter((item) =>
        showHiddenApps ? item.hidden === true : !item.hidden
      ),
    [integrationList, showHiddenApps]
  );

  /**
   * Determine if empty state should render (after first fetch, not loading, not skip)
   */
  useEffect(() => {
    if (
      hasFetched &&
      !isFetching &&
      !isLoading &&
      !skip &&
      visibleIntegrations.length === 0
    ) {
      setShowEmptyState(true);
    } else {
      setShowEmptyState(false);
    }
  }, [hasFetched, isFetching, isLoading, skip, visibleIntegrations]);

  /**
   * Infinite-scroll: observe last rendered item to load the next page
   */
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || isFetching || data?.nextPage == null) {
        return;
      }

      observer.current?.disconnect();
      observer.current = new IntersectionObserver(([entry]) => {
        if (
          entry.isIntersecting &&
          typeof data.nextPage === 'number' &&
          data.nextPage !== lastFetchedPage
        ) {
          setPage(data.nextPage);
        }
      });
      observer.current.observe(node);
    },
    [isFetching, data, lastFetchedPage]
  );

  /**
   * Context-sensitive empty state UI based on active filters/search/toggle
   */
  const renderEmptyState = () => {
    if (!hasFetched || isFetching || isLoading) {
      return null;
    }

    if (searchQuery) {
      return (
        <Box className='IntegrationAllTab-EmptyState-Search'>
          <Typography kind='h4' color='--color-foreground-neutral-subtle'>
            {`${formatMessage(
              'INTEGRATIONS.DASHBOARD.EMPTY_STATE.SEARCH',
              'No results found for'
            )} “${searchQuery}”`}
          </Typography>
        </Box>
      );
    }

    if (showHiddenApps) {
      return (
        <Box className='IntegrationAllTab-EmptyState-Hidden'>
          <Typography kind='h4' color='--color-foreground-neutral-subtle'>
            {formatMessage(
              'INTEGRATIONS.DASHBOARD.EMPTY_STATE.HIDDEN',
              'No hidden integrations available.'
            )}
          </Typography>
        </Box>
      );
    }

    if (selectedTypes) {
      const typeMap: Record<string, string> = {
        DISCOVERY_SOURCE: 'Discovery sources',
        ONBOARDED_APP: 'Onboarded applications',
        DISCOVERED_APP: 'Discovered applications',
      };

      const selectedArray = selectedTypes.split(',');
      const labels = selectedArray
        .map((type) => typeMap[type] || type)
        .join(', ');

      const isSingleType = selectedArray.length === 1;
      const message = isSingleType
        ? `No ${labels} are added`
        : `No integrations found for ${labels}.`;

      return (
        <Box className='IntegrationAllTab-EmptyState-Filter'>
          <Typography kind='h4' color='--color-foreground-neutral-subtle'>
            {message}
          </Typography>
        </Box>
      );
    }

    return (
      <Box className='IntegrationAllTab-EmptyState'>
        <Typography kind='h4' color='--color-foreground-neutral-subtle'>
          <IntegrationAllTabStaticCards />
        </Typography>
      </Box>
    );
  };

  /**
   * Render skeleton loader when initial fetch happens
   */
  const renderSkeletons = () =>
    Array.from({ length: 8 }, (_, i) => (
      <div className='IntegrationAllTab-List-Item' key={i}>
        <IntegrationsSkeletonLoader />
      </div>
    ));

  return (
    <>
      <SearchFilterViewBar />

      <AlertBanner
        colorTheme='Critical'
        title={formatMessage(
          'INTEGRATIONS.ALERT.TITLE',
          'Something went wrong'
        )}
        description={alertMessage}
        isVisible={alertVisible}
        onCancel={() => setAlertVisible(false)}
        isFloating
        shouldAutoDismiss
        autoDismissDelay={5000}
      />

      <div className='IntegrationAllTab'>
        <Box className='IntegrationAllTab-List'>
          {/* Skeleton Loader for first load */}
          {isFetching && integrationList.length === 0 && renderSkeletons()}

          {/* Render list or empty state */}
          {visibleIntegrations.map((item, idx) => (
            <div
              key={item.id}
              ref={
                idx === visibleIntegrations.length - 1 ? lastElementRef : null
              }
              className='IntegrationAllTab-List-Item'>
              <ListRow
                leadingComp={
                  <ImageWithFallback
                    src={
                      item.iconLink
                        ? `${baseUrl}${GET_APPLICATION_ICONS}/${item.iconLink}`
                        : undefined
                    }
                    alt='Application icon'
                  />
                }
                centerContent={
                  <Box className='IntegrationAllTab-CenterContent'>
                    <Box>
                      <Typography
                        kind='h3'
                        lineLimit={1}
                        color='--color-foreground-neutral-bold'>
                        {item.name}
                      </Typography>
                    </Box>
                    <Box>
                      <ItemContent
                        item={item}
                        statusLoading={statusLoadingIds.has(item.id)}
                      />
                    </Box>
                  </Box>
                }
                trailingContent={
                  <TrailingContent
                    item={item}
                    setIntegrationList={setIntegrationList}
                    setStatusLoadingIds={setStatusLoadingIds}
                    setAlertVisible={setAlertVisible}
                    setAlertMessage={setAlertMessage}
                  />
                }
                isListItem={false}
                isCheckboxDisabled
              />
            </div>
          ))}

          {/* Empty state */}
          {showEmptyState && !isFetching && !isLoading && renderEmptyState()}

          {/* Loader */}
          {isFetching && integrationList.length !== 0 && (
            <div
              className='IntegrationAllTab-List-loader'
              data-testid='loader-loop'>
              <Loader kind='loop' format='main' color='brand' />
            </div>
          )}
        </Box>
      </div>
    </>
  );
}

export default IntegrationsPageAllTab;
